// Seções reutilizáveis pra gerenciar grants user↔banco.
// - <BankGrantsForBank bank /> dentro do modal de Editar banco
// - <BankGrantsForUser user /> dentro do modal de Editar usuário
//
// Cada uma carrega a própria lista, faz add/remove via API, e oferece toggle
// canTakeExam (acesso só-estudo vs. acesso completo).

import { useEffect, useMemo, useState } from "preact/hooks";
import {
  Archive,
  Check,
  CheckCircle2,
  Database,
  GraduationCap,
  Loader2,
  Search,
  Trash2,
  UserPlus,
  Users
} from "lucide-react";
import {
  api,
  type BankGrantBankEntry,
  type BankGrantUserEntry,
  type BankListItem,
  type UserRow
} from "./api";
import { openAlert } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

// =========================================================================
// Lado do BANCO: lista usuários com grant direto neste banco
// =========================================================================

export function BankGrantsForBank({
  bank,
  allUsers
}: {
  bank: BankListItem;
  allUsers: UserRow[];
}) {
  const [entries, setEntries] = useState<BankGrantUserEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const res = await api.grantsByBank(bank.id);
      setEntries(res.entries);
    } catch (err) {
      void openAlert({ title: "Erro", message: friendlyError(err, { fallback: "Falha ao carregar acessos" }) });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [bank.id]);

  async function addUser(userId: string) {
    setBusy(userId);
    try {
      await api.putGrant({ bankId: bank.id, userId, canTakeExam: true });
      await load();
      setPicking(false);
    } catch (err) {
      void openAlert({ title: "Erro", message: friendlyError(err, { fallback: "Falha ao adicionar" }) });
    } finally {
      setBusy(null);
    }
  }

  async function toggleExam(entry: BankGrantUserEntry) {
    setBusy(entry.userId);
    try {
      await api.putGrant({
        bankId: bank.id,
        userId: entry.userId,
        canTakeExam: !entry.canTakeExam
      });
      await load();
    } catch (err) {
      void openAlert({ title: "Erro", message: friendlyError(err, { fallback: "Falha ao atualizar" }) });
    } finally {
      setBusy(null);
    }
  }

  async function revoke(entry: BankGrantUserEntry) {
    if (!confirm(`Revogar acesso de ${entry.email} a este banco?`)) return;
    setBusy(entry.userId);
    try {
      await api.deleteGrant(bank.id, entry.userId);
      await load();
    } catch (err) {
      void openAlert({ title: "Erro", message: friendlyError(err, { fallback: "Falha ao revogar" }) });
    } finally {
      setBusy(null);
    }
  }

  const grantedSet = new Set(entries?.map((e) => e.userId) ?? []);

  return (
    <section class="space-y-2 rounded-md border border-border bg-card/40 p-3">
      <header class="flex items-center justify-between gap-2">
        <h3 class="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          <Users width="1em" height="1em" />
          Acessos diretos
          {entries && (
            <span class="rounded bg-accent/60 px-1 text-[10px] tabular-nums">
              {entries.length}
            </span>
          )}
        </h3>
        {!picking && (
          <button
            type="button"
            onClick={() => setPicking(true)}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[10px] font-semibold hover:bg-accent"
          >
            <UserPlus width="0.9em" height="0.9em" />
            Adicionar usuário
          </button>
        )}
      </header>

      <p class="text-[10px] text-muted-foreground/80">
        Independente de owner / Pública / disciplina. Default permite prova
        simulada — desabilite a "prova" para acesso só-estudo.
      </p>

      {loading ? (
        <div class="flex items-center gap-2 px-2 py-3 text-[11px] text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" /> Carregando…
        </div>
      ) : entries && entries.length === 0 && !picking ? (
        <p class="rounded-md border border-dashed border-border bg-background/30 px-3 py-3 text-center text-[11px] italic text-muted-foreground">
          Nenhum acesso direto. Acesso ainda funciona via owner / Pública / disciplina.
        </p>
      ) : (
        <ul class="space-y-1">
          {entries?.map((e) => (
            <li
              key={e.userId}
              class="flex items-center gap-2 rounded-md border border-border bg-background/50 px-2 py-1.5 text-xs"
            >
              <div class="min-w-0 flex-1">
                <div class="truncate font-semibold">{e.name}</div>
                <div class="truncate font-mono text-[10px] text-muted-foreground">
                  {e.email}
                </div>
              </div>
              <button
                type="button"
                onClick={() => toggleExam(e)}
                disabled={busy === e.userId}
                title={e.canTakeExam ? "Pode iniciar prova simulada" : "Só estudo"}
                aria-label={e.canTakeExam ? "Pode iniciar prova simulada" : "Só estudo"}
                class={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider transition-colors disabled:opacity-50 ${
                  e.canTakeExam
                    ? "bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/25 dark:text-emerald-300"
                    : "bg-amber-500/15 text-amber-700 hover:bg-amber-500/25 dark:text-amber-300"
                }`}
              >
                <GraduationCap width="0.9em" height="0.9em" />
                {e.canTakeExam ? "Estudo + prova" : "Só estudo"}
              </button>
              <button
                type="button"
                onClick={() => revoke(e)}
                disabled={busy === e.userId}
                title="Revogar acesso"
                aria-label="Revogar acesso"
                class="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
              >
                {busy === e.userId ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Trash2 width="0.95em" height="0.95em" />
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {picking && (
        <UserPicker
          allUsers={allUsers}
          excludeIds={grantedSet}
          busyId={busy}
          onPick={addUser}
          onCancel={() => setPicking(false)}
        />
      )}
    </section>
  );
}

function UserPicker({
  allUsers,
  excludeIds,
  busyId,
  onPick,
  onCancel
}: {
  allUsers: UserRow[];
  excludeIds: Set<string>;
  busyId: string | null;
  onPick: (id: string) => void;
  onCancel: () => void;
}) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const candidates = allUsers.filter((u) => !excludeIds.has(u.id));
    if (!term) return candidates.slice(0, 30);
    return candidates
      .filter((u) =>
        `${u.email} ${u.name} ${u.apelido ?? ""}`.toLowerCase().includes(term)
      )
      .slice(0, 30);
  }, [allUsers, excludeIds, q]);

  return (
    <div class="space-y-2 rounded-md border border-primary/40 bg-primary/5 p-2">
      <div class="relative">
        <Search
          width="1em"
          height="1em"
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <input
          type="search"
          value={q}
          onInput={(e) => setQ((e.target as HTMLInputElement).value)}
          placeholder="Buscar usuário por email, nome…"
          autoFocus
          class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </div>
      <div class="max-h-[240px] overflow-y-auto rounded-md border border-border bg-background">
        {filtered.length === 0 ? (
          <p class="p-3 text-center text-xs italic text-muted-foreground">
            Nenhum usuário restante.
          </p>
        ) : (
          <ul class="divide-y divide-border">
            {filtered.map((u) => (
              <li
                key={u.id}
                class="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-accent/30"
              >
                <div class="min-w-0 flex-1">
                  <div class="truncate font-semibold">{u.name}</div>
                  <div class="truncate font-mono text-[10px] text-muted-foreground">
                    {u.email}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onPick(u.id)}
                  disabled={busyId === u.id}
                  class="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-[10px] font-bold text-white hover:brightness-110 disabled:opacity-50"
                >
                  {busyId === u.id ? (
                    <Loader2 className="size-3 animate-spin" />
                  ) : (
                    <Check width="0.9em" height="0.9em" />
                  )}
                  Conceder
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <button
        type="button"
        onClick={onCancel}
        class="text-[11px] text-muted-foreground hover:text-foreground underline"
      >
        Cancelar
      </button>
    </div>
  );
}

// =========================================================================
// Lado do USUÁRIO: lista bancos que esse user tem grant direto
// =========================================================================

export function BankGrantsForUser({
  user,
  allBanks
}: {
  user: { id: string };
  allBanks: BankListItem[];
}) {
  const [entries, setEntries] = useState<BankGrantBankEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const res = await api.grantsByUser(user.id);
      setEntries(res.entries);
    } catch (err) {
      void openAlert({ title: "Erro", message: friendlyError(err, { fallback: "Falha ao carregar bancos" }) });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [user.id]);

  async function addBank(bankId: string) {
    setBusy(bankId);
    try {
      await api.putGrant({ bankId, userId: user.id, canTakeExam: true });
      await load();
      setPicking(false);
    } catch (err) {
      void openAlert({ title: "Erro", message: friendlyError(err, { fallback: "Falha ao adicionar" }) });
    } finally {
      setBusy(null);
    }
  }

  async function toggleExam(entry: BankGrantBankEntry) {
    setBusy(entry.bankId);
    try {
      await api.putGrant({
        bankId: entry.bankId,
        userId: user.id,
        canTakeExam: !entry.canTakeExam
      });
      await load();
    } catch (err) {
      void openAlert({ title: "Erro", message: friendlyError(err, { fallback: "Falha ao atualizar" }) });
    } finally {
      setBusy(null);
    }
  }

  async function revoke(entry: BankGrantBankEntry) {
    if (!confirm(`Revogar acesso a "${entry.name}"?`)) return;
    setBusy(entry.bankId);
    try {
      await api.deleteGrant(entry.bankId, user.id);
      await load();
    } catch (err) {
      void openAlert({ title: "Erro", message: friendlyError(err, { fallback: "Falha ao revogar" }) });
    } finally {
      setBusy(null);
    }
  }

  const grantedSet = new Set(entries?.map((e) => e.bankId) ?? []);

  return (
    <section class="space-y-2 rounded-md border border-border bg-card/40 p-3">
      <header class="flex items-center justify-between gap-2">
        <h3 class="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          <Database width="1em" height="1em" />
          Bancos acessíveis (grant direto)
          {entries && (
            <span class="rounded bg-accent/60 px-1 text-[10px] tabular-nums">
              {entries.length}
            </span>
          )}
        </h3>
        {!picking && (
          <button
            type="button"
            onClick={() => setPicking(true)}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[10px] font-semibold hover:bg-accent"
          >
            <CheckCircle2 width="0.9em" height="0.9em" />
            Conceder banco
          </button>
        )}
      </header>

      <p class="text-[10px] text-muted-foreground/80">
        Bancos liberados sem precisar de Pública / disciplina. Mostra também os
        arquivados (admin pode revogar arquivando o banco).
      </p>

      {loading ? (
        <div class="flex items-center gap-2 px-2 py-3 text-[11px] text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" /> Carregando…
        </div>
      ) : entries && entries.length === 0 && !picking ? (
        <p class="rounded-md border border-dashed border-border bg-background/30 px-3 py-3 text-center text-[11px] italic text-muted-foreground">
          Sem grants diretos. Usuário só vê bancos via Pública / disciplina /
          owner.
        </p>
      ) : (
        <ul class="space-y-1">
          {entries?.map((e) => (
            <li
              key={e.bankId}
              class={`flex items-center gap-2 rounded-md border border-border bg-background/50 px-2 py-1.5 text-xs ${
                e.archived ? "opacity-60" : ""
              }`}
            >
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-1 truncate font-semibold">
                  {e.archived && (
                    <Archive width="0.9em" height="0.9em" className="text-amber-600 dark:text-amber-400" />
                  )}
                  {e.name}
                </div>
                <div class="font-mono text-[10px] text-muted-foreground">
                  {new Date(e.grantedAt).toLocaleString("pt-BR")}
                </div>
              </div>
              <button
                type="button"
                onClick={() => toggleExam(e)}
                disabled={busy === e.bankId}
                title={e.canTakeExam ? "Pode iniciar prova simulada" : "Só estudo"}
                aria-label={e.canTakeExam ? "Pode iniciar prova simulada" : "Só estudo"}
                class={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider transition-colors disabled:opacity-50 ${
                  e.canTakeExam
                    ? "bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/25 dark:text-emerald-300"
                    : "bg-amber-500/15 text-amber-700 hover:bg-amber-500/25 dark:text-amber-300"
                }`}
              >
                <GraduationCap width="0.9em" height="0.9em" />
                {e.canTakeExam ? "Estudo + prova" : "Só estudo"}
              </button>
              <button
                type="button"
                onClick={() => revoke(e)}
                disabled={busy === e.bankId}
                title="Revogar acesso"
                aria-label="Revogar acesso"
                class="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
              >
                {busy === e.bankId ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Trash2 width="0.95em" height="0.95em" />
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {picking && (
        <BankPicker
          allBanks={allBanks}
          excludeIds={grantedSet}
          busyId={busy}
          onPick={addBank}
          onCancel={() => setPicking(false)}
        />
      )}
    </section>
  );
}

function BankPicker({
  allBanks,
  excludeIds,
  busyId,
  onPick,
  onCancel
}: {
  allBanks: BankListItem[];
  excludeIds: Set<string>;
  busyId: string | null;
  onPick: (id: string) => void;
  onCancel: () => void;
}) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const candidates = allBanks.filter(
      (b) => !excludeIds.has(b.id) && !b.archivedAt
    );
    if (!term) return candidates.slice(0, 30);
    return candidates
      .filter((b) =>
        `${b.name} ${b.description ?? ""} ${b.companyName ?? ""} ${b.tags.join(" ")}`
          .toLowerCase()
          .includes(term)
      )
      .slice(0, 30);
  }, [allBanks, excludeIds, q]);

  return (
    <div class="space-y-2 rounded-md border border-primary/40 bg-primary/5 p-2">
      <div class="relative">
        <Search
          width="1em"
          height="1em"
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <input
          type="search"
          value={q}
          onInput={(e) => setQ((e.target as HTMLInputElement).value)}
          placeholder="Buscar banco por nome, descrição, company, tag…"
          autoFocus
          class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </div>
      <div class="max-h-[240px] overflow-y-auto rounded-md border border-border bg-background">
        {filtered.length === 0 ? (
          <p class="p-3 text-center text-xs italic text-muted-foreground">
            Nenhum banco restante.
          </p>
        ) : (
          <ul class="divide-y divide-border">
            {filtered.map((b) => (
              <li
                key={b.id}
                class="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-accent/30"
              >
                <div class="min-w-0 flex-1">
                  <div class="truncate font-semibold">{b.name}</div>
                  <div class="truncate text-[10px] text-muted-foreground">
                    {b.companyName ?? "Sem company"} · {b.questionCount} questões
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onPick(b.id)}
                  disabled={busyId === b.id}
                  class="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-[10px] font-bold text-white hover:brightness-110 disabled:opacity-50"
                >
                  {busyId === b.id ? (
                    <Loader2 className="size-3 animate-spin" />
                  ) : (
                    <Check width="0.9em" height="0.9em" />
                  )}
                  Conceder
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <button
        type="button"
        onClick={onCancel}
        class="text-[11px] text-muted-foreground hover:text-foreground underline"
      >
        Cancelar
      </button>
    </div>
  );
}
